import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import type { WorkspaceRow } from './workspace';
import { rowKey } from './workspace-dnd-rules';
import { WorkspaceDragPreview } from './workspace-drag-preview';

type Point = { clientX: number; clientY: number };

export function useWorkspaceDragPreview() {
  const [rows, setRows] = useState<WorkspaceRow[]>([]);
  const element = useRef<HTMLDivElement>(null);
  const position = useRef('');
  const height = useRef(0);
  const active = useRef(false);
  const nativeImage = useRef<HTMLCanvasElement>();

  const move = ({ clientX, clientY }: Point) => {
    if (!active.current) return;
    const x = clientX + 300 > window.innerWidth ? clientX - 298 : clientX + 18;
    const y =
      clientY + height.current + 18 > window.innerHeight
        ? clientY - height.current - 18
        : clientY + 18;
    position.current = `translate3d(${Math.max(12, x)}px, ${Math.max(12, y)}px, 0)`;
    if (element.current) element.current.style.transform = position.current;
  };
  const clear = () => {
    if (!active.current) return;
    active.current = false;
    nativeImage.current?.remove();
    nativeImage.current = undefined;
    setRows([]);
  };
  useEffect(() => {
    // HTML5 dragging suppresses mousemove. Track dragover across the page,
    // including empty space, without rerendering the grid on every frame.
    window.addEventListener('dragover', move, true);
    window.addEventListener('drop', clear, true);
    window.addEventListener('dragend', clear, true);
    window.addEventListener('blur', clear);
    return () => {
      window.removeEventListener('dragover', move, true);
      window.removeEventListener('drop', clear, true);
      window.removeEventListener('dragend', clear, true);
      window.removeEventListener('blur', clear);
      nativeImage.current?.remove();
    };
  }, []);

  const start = (
    items: WorkspaceRow[],
    point: Point,
    transfer?: DataTransfer
  ) => {
    active.current = true;
    height.current =
      66 + Math.min(items.length, 3) * 44 + (items.length > 3 ? 36 : 0);
    move(point);
    setRows(items);
    if (transfer?.setDragImage) {
      // Replace the browser's single-row ghost with the shared group preview.
      const image = document.createElement('canvas');
      image.width = image.height = 1;
      image.style.position = 'fixed';
      image.style.left = image.style.top = '0';
      image.style.pointerEvents = 'none';
      document.body.append(image);
      transfer.setDragImage(image, 0, 0);
      nativeImage.current?.remove();
      nativeImage.current = image;
    }
  };

  return {
    start,
    move,
    clear,
    isDragging: (row: WorkspaceRow) =>
      rows.some(item => rowKey(item) === rowKey(row)),
    preview: rows.length
      ? createPortal(
          <WorkspaceDragPreview
            rows={rows}
            previewRef={element}
            transform={position.current}
          />,
          document.body
        )
      : null
  };
}
